import {
  Button,
  Chip,
  Link,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import { FulfillmentStatus, OrderLineItemDto } from "@lib/ShiOneClient";
import { useMemo } from "react";
import { createSavedCurrencyFormatter } from "shared-ui/src/functions/currency";
import { navigateToShiCom } from "@lib/shiComBaseUrl";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderViewType } from "@widgets/ecommerce/types/OrderViewType";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { alpha } from "@mui/material/styles";
import { useOrderDrawer } from "@features/orders-features/components/order-drawer";
import { StackedTrackingDrawer } from "@features/orders-features/components/order-drawer/StackedTrackingDrawer";
import { getOrderStatusChip } from "@features/orders-features/components/orderStatusChips";
import { TRACKING_DRAWER_SEGMENT } from "@features/orders-features/components/order-drawer/orderDrawerSegments";
import {
  buildTrackingDrawerProps,
  toTrackingRouteId,
} from "@features/orders-features/components/order-drawer/trackingDrawerProps";
import { groupItemsByShipment } from "@features/orders-features/components/order-detail-panel/ShipmentMeta";
import { pascalToTitleCase } from "@features/orders-features/functions/helpers";
import { generateOrderDetailHref } from "@features/orders-features/functions/orderHelpers";
import { OrderRow, ShipTo } from "@features/orders-features/types/types";
import { useDrawerStack } from "shared-ui";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { AppendIfPrefixExists } from "../../../../../modules/sitePaths";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { getApprovalReviewHref } from "@features/orders-features/components/order-approval-drawer/approvalReviewRoute";

type OrderAction = "VIEW" | "TRACK";

// Canonical FulfillmentStatus values that represent an in-transit / trackable order.
const TRACK_STATUSES = new Set<string>([
  FulfillmentStatus.PartiallyShipped,
  FulfillmentStatus.Shipped,
  FulfillmentStatus.Delivered,
  FulfillmentStatus.Returned,
  FulfillmentStatus.PendingReturn,
]);

// Canonical FulfillmentStatus values that route a web order into the approval flow.
const APPROVAL_STATUSES = new Set<string>([
  FulfillmentStatus.AwaitingApproval,
  FulfillmentStatus.TransferApprover,
]);

const isApprovalStatus = (status: string | undefined) =>
  !!status && APPROVAL_STATUSES.has(status);

const getAction = (status: string | undefined): OrderAction =>
  status && TRACK_STATUSES.has(status) ? "TRACK" : "VIEW";

function OrderStatusChip({ status }: Readonly<{ status?: string }>) {
  const { color, icon, iconColor, borderColor } = getOrderStatusChip(
    status as FulfillmentStatus | undefined,
  );

  return (
    <Chip
      icon={icon}
      label={status ? pascalToTitleCase(status) : "Unknown"}
      size="small"
      variant="outlined"
      sx={{
        borderColor: borderColor ?? color,
        color: "text.primary",
        "& .MuiChip-icon": {
          color: iconColor ?? color,
        },
      }}
    />
  );
}

const formatProductCount = (count?: number) => {
  if (count === undefined) return "0 products";
  if (count === 1) return "1 product";
  return `${count} products`;
};

const getShipToDisplay = (shipTo?: ShipTo) =>
  shipTo?.contact ?? shipTo?.recipient ?? shipTo?.address?.join(", ") ?? "—";

// Order line items are already grouped by display status (see OrderDrawerDetails);
// only the physical fulfillment statuses carry shipment/tracking data.
const isShippingSectionTitle = (title: string) =>
  title === FulfillmentStatus.Shipped || title === FulfillmentStatus.Delivered;

const getShipmentGroups = (order: OrderRow) => {
  const productsByStatus = (order.orderLineProducts ?? {}) as Record<
    string,
    OrderLineItemDto[]
  >;
  const shippingItems = Object.entries(productsByStatus)
    .filter(([status, items]) => items?.length > 0 && isShippingSectionTitle(status))
    .flatMap(([, items]) => items);

  if (shippingItems.length === 0) return [];

  return groupItemsByShipment(shippingItems);
};

const getSummary = (
  view: OrderViewType,
  order: OrderRow,
  currencyFormatter: Intl.NumberFormat,
) => {
  const a = [];
  if (view !== OrderViewType.My) {
    a.push("Ordered by");
    a.push(order.orderedBy || "—");
  }
  a.push("Ship to");
  a.push(getShipToDisplay(order.shipTo));
  a.push(formatProductCount(order.totalItems));
  if (order.total) {
    a.push(currencyFormatter.format(order.total));
  }
  return a.join(" ");
};

export interface OrderProps {
  order: OrderRow;
  type: OrderViewType;
}

export default function Order({ order, type }: Readonly<OrderProps>) {
  const action = getAction(order.status);
  const { hasPermission } = useClaims();
  const { openOrderDrawer, isAvailable } = useOrderDrawer();
  const { pushDrawer } = useDrawerStack();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    enabled: orderApprovalsEnabled,
    loading: orderApprovalsLoading,
  } = useFeatureFlag("nomads.order-approvals", false);

  const { href: orderDetailHref } = generateOrderDetailHref(
    order.biOrderId,
    order.salesOrder,
    order.webOrderId,
  );

  const isApprovalOrder =
    (order.biOrderId == null || order.biOrderId == 0 )
    && order.webOrderId != null
    && order.webOrderId != 0
    && isApprovalStatus(order.status);

  const canApprove = hasPermission(ShiOnePermission.webOrderApprove);

  const shouldNavigateToApproval = isApprovalOrder && canApprove;

  const currencyFormatter = useMemo(
    () => createSavedCurrencyFormatter(order.currencyCode),
    [order.currencyCode],
  );
  const summary = getSummary(type, order, currencyFormatter);

  const salesOrder = order.salesOrder || order.webOrderId?.toString();

  const openDrawerForOrder = () => {
    if (isAvailable && salesOrder) {
      openOrderDrawer(
        {
          salesOrder,
          biOrderId: order.biOrderId,
          webOrderId: order.webOrderId,
          orgUnitId: order.orgUnitId,
          crmAccountId: order.crmAccountId,
        },
        {
          orderDate: order.orderDate,
          status: order.status,
          purchaseOrder: order.purchaseOrder,
          orderedBy: order.orderedBy,
          shipTo: order.shipTo,
          totalItems: order.totalItems,
          total: order.total,
          currencyCode: order.currencyCode,
          invoiceStatus: order.invoiceStatus,
          orderLineProducts: order.orderLineProducts,
          lineItems: order.allLineItems,
          canReturn: order.canReturn,
        },
      );
    } else if (orderDetailHref) {
      navigate(orderDetailHref, {
        state: { listUrl: `${location.pathname}${location.search}` },
      });
    }
  };

  const openTrackDrawerForOrder = () => {
    const shipmentGroups = getShipmentGroups(order);
    if (shipmentGroups.length > 1) {
      openDrawerForOrder();
      return;
    }

    const group =
      shipmentGroups.find((shipmentGroup) => shipmentGroup.trackingNumber) ??
      shipmentGroups[0];
    const props =
      group &&
      buildTrackingDrawerProps({ ...order, salesOrder }, group, order.status);
    if (!props) {
      openDrawerForOrder();
      return;
    }
    const routeId = toTrackingRouteId(order, group.trackingNumber);

    pushDrawer({
      id: `tracking-${props.trackingNumber}`,
      component: StackedTrackingDrawer,
      props,
      ...(routeId && {
        route: { segment: TRACKING_DRAWER_SEGMENT, id: routeId },
      }),
    });
  };

  const handleActionClick = () => {
    if (shouldNavigateToApproval) {
      if (orderApprovalsEnabled && !orderApprovalsLoading) {
        navigate(
          AppendIfPrefixExists(
            getApprovalReviewHref(order.webOrderId),
          ),
        );
      } else {
        navigateToShiCom(
          `/Checkout/WebOrderapprove.aspx?weborderID=${order.webOrderId}`,
        );
      }
      return;
    }

    if (action === "TRACK") {
      openTrackDrawerForOrder();
    } else {
      openDrawerForOrder();
    }
  };

  return (
    <ListItem
      divider={true}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <ListItemText
        sx={{ minWidth: 0, flex: "1 1 auto", overflow: "hidden" }}
        primary={
          <Link
            component="button"
            type="button"
            underline="hover"
            {...telemetryProps({
              name: "orders_recent_so_link",
              section: "orders_recent_widget",
              label: "Open order",
              ...(salesOrder ? { id: salesOrder } : {}),
            })}
            onClick={(e) => {
              e.stopPropagation();
              openDrawerForOrder();
            }}
            disabled={!isAvailable && !orderDetailHref}
            sx={[
              {
                fontWeight: 500,
                display: "inline-block",
                borderRadius: "8px",
                padding: "4px 8px",
                margin: "-4px -8px",
                transition: "background-color 0.2s, opacity 0.2s",
              },
              (theme) =>
                theme.applyStyles("light", {
                  "&:focus-visible": {
                    outline: "none",
                    backgroundColor: alpha(
                      theme.palette.primary.dark,
                      theme.palette.action.focusOpacity,
                    ),
                  },
                }),
              (theme) =>
                theme.applyStyles("dark", {
                  "&:focus-visible": {
                    outline: "none",
                    backgroundColor: alpha(
                      theme.palette.primary.light,
                      theme.palette.action.focusOpacity,
                    ),
                  },
                }),
            ]}
          >
            {salesOrder}
          </Link>
        }
        slotProps={{
          primary: {
            variant: "body2",
          },
          secondary: {
            noWrap: true,
            variant: "caption",
            color: "text.secondary",
            sx: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          },
        }}
        secondary={summary}
      />
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flexShrink: 0 }}
      >
        <OrderStatusChip status={order.status} />
        <Button
          variant="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleActionClick();
          }}
          disabled={
            !shouldNavigateToApproval && !isAvailable && !orderDetailHref
          }
          {...telemetryProps({
            name: "orders_recent_view_track",
            section: "orders_recent_widget",
            label: action === "TRACK" ? "Track order" : "View order",
            ...(salesOrder ? { id: salesOrder } : {}),
          })}
        >
          {action}
        </Button>
      </Stack>
    </ListItem>
  );
}
